import type { SVGProps } from 'react';

/**
 * Logo canônica da Storeo — SVG de `docs/prototype/storeo_logo/code.html` (redesign Lumina).
 * Substitui os `<img src="lh3.googleusercontent.com/...">` que o Stitch exporta nas telas.
 *
 * `variant="full"` — ícone + wordmark "storeo." (240×60, proporção do protótipo).
 * `variant="mark"` — só o ícone (quadrado), para espaços compactos / o "orbe" do login.
 *
 * Sem hooks, para poder rodar em Server Component (storefront) e no Vite (painel). Os IDs
 * de gradiente são estáticos: se a página tiver duas instâncias, elas compartilham defs
 * idênticas — sem impacto visual. Passe `id` diferente se precisar isolar.
 */
export function StoreoLogo({
  variant = 'full',
  id = 'storeo-logo',
  ...props
}: SVGProps<SVGSVGElement> & { variant?: 'full' | 'mark'; id?: string }) {
  const grad = `${id}-grad`;
  const glow = `${id}-blur`;

  const defs = (
    <defs>
      <linearGradient id={grad} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6366F1" />
        <stop offset="50%" stopColor="#8B5CF6" />
        <stop offset="100%" stopColor="#06B6D4" />
      </linearGradient>
      <filter id={glow} x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="3" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>
  );

  const icon = (
    <>
      <rect x="0" y="0" width="40" height="40" rx="12" fill="#181B25" stroke="#2D3245" strokeWidth="1.5" />
      <path
        d="M12 28 C12 22, 16 16, 24 14 C28 13, 30 15, 28 19 C25 24, 18 25, 28 27 C31 28, 30 31, 26 31 C19 31, 14 30, 12 28 Z"
        fill={`url(#${grad})`}
        filter={`url(#${glow})`}
      />
      <circle cx="28" cy="14" r="3.5" fill="#06B6D4" />
      <circle cx="13" cy="27" r="2.5" fill="#8B5CF6" />
    </>
  );

  if (variant === 'mark') {
    return (
      <svg viewBox="0 0 40 40" width="40" height="40" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        {defs}
        {icon}
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 240 60" width="240" height="60" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      {defs}
      <g transform="translate(10, 10)">{icon}</g>
      <text
        x="62"
        y="37"
        fontFamily="'Plus Jakarta Sans', system-ui, -apple-system, sans-serif"
        fontWeight="800"
        fontSize="24"
        fill="#FFFFFF"
        letterSpacing="-0.5"
      >
        storeo
        <tspan fill="#6366F1">.</tspan>
      </text>
    </svg>
  );
}
